import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Security } from '../../../../proto/generated/security'
import { ColorType, createChart, IChartApi, ISeriesApi, PriceScaleMode, Time } from 'lightweight-charts'
import { formatLargeNumber } from '../../../../util/screener'
import { GroupedBarsSeries } from './barseries/grouped-bars-series'
import { CrosshairHighlightPrimitive } from './barseries/example/highlight-bar-crosshair'
import { Button, Typography, useTheme } from '@mui/material'
import SettingsOutlinedIcon from '../../../../assets/icons/SettingOutlined'
import { NonInteractiveTooltip } from '../../../custom'
import { chartLayoutOptions } from '../../../../assets/constants'
import OiChartMetaData, { OiChartMetaDataHandle } from './oiChartMetaData'
import { VertLine } from './barseries/verticalLine'
import { useResizeDetector } from 'react-resize-detector'
import ChartSettingsButtons from '../../../chartV2/chartContextMenuButton/ChartSettingsButtons'
import { AppContext } from '../../../../App'
import { useSelector } from 'react-redux'
import { selectMultiStrikeCanvasSettings, selectOiWidgetSettings } from '../../../../selectors/chart'
import { buildCanvasLayoutPartial } from '../../../../util/canvasSettingsDefaults'
import OiChartSkeletonOverlay from '../../../common/OiChartSkeletonOverlay'

export interface OiWidgetDataType {
    [key: string]: number | number[] | undefined

    time: number | undefined
    values: number[]
}

type Props = {
    chartId: string
    loading: boolean
    allowSplit?: boolean,
    data: OiWidgetDataType[]
    atmStrike: number | undefined
    setSelectedExpiry: React.Dispatch<React.SetStateAction<{
        expirySeconds: string;
        expiry: string;
    } | undefined>>
    onSettingsClick?: () => void
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
}

const OiChartNew = (props: Props) => {
    const {
        chartId,
        loading,
        security,
        data,
        selectedExpiry,
        setSelectedExpiry,
        atmStrike,
        allowSplit,
        onSettingsClick,
        onSecuritySelected
    } = props
    const { width, height, ref: resizeRef } = useResizeDetector()
    const containerRef = useRef<HTMLDivElement | null>(null)
    const appContext = useContext(AppContext)
    const chartRef = useRef<IChartApi | null>(null)
    const iChartSeriesApi = useRef<ISeriesApi<any> | null>(null)
    const memoizedChartId = useMemo(() => chartId, [chartId])
    const memoizedSecurity = useMemo(() => security, [security])
    const canvasSettings = useSelector(selectMultiStrikeCanvasSettings(chartId))
    const oiWidgetSettings = useSelector(selectOiWidgetSettings(chartId))
    const theme = useTheme()
    const metaDataRef = useRef<OiChartMetaDataHandle>(null)
    const [scaleDims, setScaleDims] = useState({ width: 52, height: 28 })
    const dataRef = useRef(data)
    const atmStrikeRef = useRef(atmStrike)
    dataRef.current = data
    atmStrikeRef.current = atmStrike

    const applyCrosshairToMetadata = useCallback((strike: number, callOi: number, putOi: number) => {
        metaDataRef.current?.updateCrosshair(
            strike,
            formatLargeNumber(callOi, 2),
            formatLargeNumber(putOi, 2)
        )
    }, [])

    const applyAtmCrosshair = useCallback(() => {
        const currentData = dataRef.current
        const currentAtm = atmStrikeRef.current
        const foundData = currentData.find(d => d.time === Number(currentAtm))
        applyCrosshairToMetadata(
            currentAtm ?? 0,
            foundData?.values?.[0] ?? 0,
            foundData?.values?.[1] ?? 0
        )
    }, [applyCrosshairToMetadata])

    const renderedFirstTime = useRef(false)
    const atmTagRef = useRef<HTMLDivElement | null>(null)

    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        containerRef.current = node
        resizeRef(node)
    }, [resizeRef])

    const measureScaleDims = useCallback(() => {
        const api = chartRef.current
        if (!api) return
        try {
            const scaleWidth = api.priceScale('right').width()
            const timeScaleHeight = api.timeScale().height()
            setScaleDims((prev) => {
                const width = scaleWidth > 0 ? scaleWidth : prev.width
                const height = timeScaleHeight > 0 ? timeScaleHeight : prev.height
                return width === prev.width && height === prev.height ? prev : { width, height }
            })
        } catch {
            // chart not ready yet
        }
    }, [])

    const layoutOptions = useMemo(() => {
        const canvasLayout = buildCanvasLayoutPartial(canvasSettings, {
            textSecondary: theme.palette.text.secondary,
            divider: theme.palette.divider,
        }, {
            alignLabels: true,
            includeLeftPriceScale: false,
        })
        return {
            ...chartLayoutOptions,
            height: height,
            width: width,
            ...canvasLayout,
            layout: {
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper
                },
                fontFamily: `'Work Sans', sans-serif`,
                ...canvasLayout.layout,
            },
            timeScale: {
                borderVisible: false,
                borderColor: theme.palette.divider,
                ...canvasLayout.timeScale,
            },
            rightPriceScale: {
                ...canvasLayout.rightPriceScale,
                borderVisible: canvasLayout.rightPriceScale?.borderVisible ?? false,
                visible: true,
                mode: PriceScaleMode.Normal
            },
            crosshair: canvasLayout.crosshair,
        }
    }, [theme, height, width, canvasSettings])

    const onCrossHairMove = useCallback((e: any) => {
        if (iChartSeriesApi.current) {
            const oiArray = e.seriesData?.get(iChartSeriesApi.current)?.values as number[] | undefined
            const strike = e?.time as number | undefined
            if (strike != null && Number.isFinite(strike) && oiArray) {
                applyCrosshairToMetadata(strike, oiArray[0] ?? 0, oiArray[1] ?? 0)
            } else {
                applyAtmCrosshair()
            }
        }
        if (chartRef.current && atmTagRef.current && atmStrikeRef.current) {
            const value = Math.min(Math.max(0, (chartRef.current?.timeScale().timeToCoordinate(atmStrikeRef.current as Time) ?? 0) - 13), (width ?? 0) - 27 - chartRef.current.priceScale('right').width())
            atmTagRef.current.style.left = `${value}px`
        }
        measureScaleDims()
    }, [applyAtmCrosshair, applyCrosshairToMetadata, width, measureScaleDims])

    useEffect(() => {
        if (renderedFirstTime.current) return

        renderedFirstTime.current = true
        const chartContainerElement = document.getElementById(chartId)
        chartRef.current = (window as unknown as any).chart = createChart(chartId, {
                width: chartContainerElement?.clientWidth,
                height: chartContainerElement?.clientHeight,
                localization: {
                    timeFormatter: (time: any) => {
                        return time.toString()
                    },
                    priceFormatter: (price: any) => {
                        return formatLargeNumber(price, 2)
                    }
                },
                timeScale: {
                    tickMarkFormatter: (time: any) => {
                        return time.toString()
                    },
                    fixLeftEdge: true,
                    fixRightEdge: true,
                    borderVisible: false
                }
            })
            appContext.setOiChart(chartId, chartRef.current)

            chartRef.current.applyOptions(layoutOptions)

            const customSeriesView = new GroupedBarsSeries()

            iChartSeriesApi.current = chartRef.current.addCustomSeries(customSeriesView as any, {
                color: 'black'
            })
            if (iChartSeriesApi.current) {
                iChartSeriesApi.current.attachPrimitive(
                    new CrosshairHighlightPrimitive({ color: 'rgba(0, 100, 200, 0.2)' }) as any
                )
                iChartSeriesApi.current.applyOptions({
                    lastValueVisible: false,
                    priceLineVisible: false
                })
            }
            const priceScale = chartRef.current.priceScale('right')

            priceScale.applyOptions({
                // autoScale: false,
                scaleMargins: {
                    top: 0.1,
                    bottom: 0
                }
            })
            requestAnimationFrame(measureScaleDims)

        chartRef.current.subscribeCrosshairMove(onCrossHairMove)
        return () => {
            chartRef.current?.unsubscribeCrosshairMove(onCrossHairMove)
        }
    }, [chartId, onCrossHairMove, appContext, measureScaleDims])

    useEffect(() => {
        chartRef.current && chartRef.current.applyOptions(layoutOptions)
        measureScaleDims()
    }, [layoutOptions, measureScaleDims])

    const histogramColors = useMemo(() => {
        const callColor = oiWidgetSettings.showCall ? oiWidgetSettings.callColor! : 'transparent'
        const putColor = oiWidgetSettings.showPut ? oiWidgetSettings.putColor! : 'transparent'
        return [callColor, putColor] as const
    }, [oiWidgetSettings.showCall, oiWidgetSettings.showPut, oiWidgetSettings.callColor, oiWidgetSettings.putColor])

    useEffect(() => {
        if (!iChartSeriesApi.current) return
        iChartSeriesApi.current.applyOptions({
            colors: histogramColors,
        })
    }, [histogramColors])

    useEffect(() => {
        if (iChartSeriesApi.current) {
            iChartSeriesApi.current.setData(data as any)
            const index = data.findIndex(d => d.time === atmStrike)
            if (index >= 0) {
                const from = data[Math.max(index - 10, 0)]
                const to = data[Math.min(index + 10, data.length - 1)]
                chartRef.current?.timeScale().setVisibleRange({
                    from: from.time as Time,
                    to: to.time as Time
                })
            }
        }
        let vertLine: VertLine
        if (chartRef.current && iChartSeriesApi.current) {
            vertLine = new VertLine(chartRef.current, iChartSeriesApi.current, (atmStrike ?? 0) as Time, {
                showLabel: false,
                color: theme.palette.primary.main
            })
            iChartSeriesApi.current.attachPrimitive(vertLine)
        }
        measureScaleDims()
        return () => {
            if (vertLine) {
                iChartSeriesApi.current?.detachPrimitive(vertLine)
            }
        }
    }, [atmStrike, data, theme, measureScaleDims])

    useEffect(() => {
        if (!containerRef.current) return
        let raf = 0
        const scheduleMeasure = () => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(measureScaleDims)
        }
        scheduleMeasure()
        const timeoutId = setTimeout(scheduleMeasure, 300)
        const observer = new ResizeObserver(scheduleMeasure)
        observer.observe(containerRef.current)
        return () => {
            cancelAnimationFrame(raf)
            clearTimeout(timeoutId)
            observer.disconnect()
        }
    }, [measureScaleDims, loading, width, height])

    useEffect(() => {
        if (chartRef.current && atmTagRef.current && atmStrike) {
            const value = Math.min(Math.max(0, (chartRef.current?.timeScale().timeToCoordinate(atmStrike as Time) ?? 0) - 13), (width ?? 0) - 27 - chartRef.current.priceScale('right').width())
            atmTagRef.current.style.left = `${value}px`
        }
    }, [atmStrike, data, width])

    useEffect(() => {
        applyAtmCrosshair()
    }, [applyAtmCrosshair, data, atmStrike])

    const handleMouseLeave = useCallback(() => {
        applyAtmCrosshair()
    }, [applyAtmCrosshair])

    return (
        <>
            <div
                style={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                }}
                onMouseLeave={handleMouseLeave}
                ref={setContainerRef}
            >
                <ChartSettingsButtons allowSplit={allowSplit} viewKey={memoizedChartId} security={memoizedSecurity} />
                {loading ? <OiChartSkeletonOverlay /> : null}
                <div
                    id={chartId}
                    style={{
                        height: '100%',
                        width: '100%',
                        margin: 0,
                        padding: 0,
                        visibility: loading ? 'hidden' : 'visible',
                    }}
                />
                <Typography ref={atmTagRef} style={{
                    color: theme.palette.primary.main,
                    padding: '0 2px',
                    fontSize: '10px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: '4px',
                    position: 'absolute',
                    background: theme.palette.background.default,
                    textWrap: 'nowrap',
                    bottom: '30px',
                    zIndex: 10,
                    visibility: atmStrike && !loading ? 'visible' : 'hidden'
                }}>
                    ATM
                </Typography>
                {onSettingsClick ? (
                    <NonInteractiveTooltip title="Settings" placement="left-start">
                        <Button
                            variant="text"
                            color="inherit"
                            disableRipple
                            onClick={onSettingsClick}
                            data-event="Oi widget Settings Clicked"
                            sx={{
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bottom: 0,
                                right: 0,
                                left: 'auto',
                                width: scaleDims.width,
                                minWidth: 52,
                                height: scaleDims.height,
                                minHeight: scaleDims.height,
                                maxHeight: scaleDims.height,
                                p: 0,
                                borderRadius: 0,
                                zIndex: 1,
                            }}
                        >
                            <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
                        </Button>
                    </NonInteractiveTooltip>
                ) : null}
            </div>
            {!loading ? (
                <OiChartMetaData
                    ref={metaDataRef}
                    viewKey={memoizedChartId}
                    security={security}
                    selectedExpiry={selectedExpiry}
                    setSelectedExpiry={setSelectedExpiry}
                    onSecuritySelected={onSecuritySelected}
                />
            ) : null}
        </>
    )
}

export default OiChartNew